import React, { useState, useEffect } from 'react';
import './Header.css';

interface HeaderProps {
  onTryForFreeClick: () => void;
  onRequestDemoClick: () => void;
  onSurveyTrigger?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onTryForFreeClick, onRequestDemoClick, onSurveyTrigger }) => {
  const [email, setEmail] = useState('');
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'submitted'>('idle');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | 'none'>('none');
  const [lastScrollY, setLastScrollY] = useState(0);

  // Load email and submission status from sessionStorage on mount
  useEffect(() => {
    const updateFromStorage = () => {
      const storedEmail = sessionStorage.getItem('userEmail');
      const isSubmitted = sessionStorage.getItem('waitlistSubmitted') === 'true';
      
      if (storedEmail) {
        setEmail(storedEmail);
      }
      
      if (isSubmitted) {
        setSubmitStatus('submitted');
      }
    };

    // Initial load
    updateFromStorage();

    // Listen for custom storage events
    const handleStorageChange = () => {
      updateFromStorage();
    };

    window.addEventListener('waitlistUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('waitlistUpdated', handleStorageChange);
    };
  }, []);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isMobileMenuOpen]);

  // Save email to sessionStorage whenever it changes
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail.trim()) {
      sessionStorage.setItem('userEmail', newEmail);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      // Get the hero section height (typically viewport height)
      const heroHeight = window.innerHeight;
      const scrollPosition = window.scrollY;
      
      // Get the waitlist section element
      const waitlistSection = document.getElementById('waitlist');
      const waitlistTop = waitlistSection?.offsetTop || Infinity;
      
      // Show waitlist form when scrolled past 80% of hero section
      // but hide it when reaching the waitlist section
      const showCondition = scrollPosition > heroHeight * 0.8 && scrollPosition < waitlistTop - 200;
      setShowWaitlist(showCondition);
    };

    window.addEventListener('scroll', handleScroll);
    
    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Detect scroll direction for mobile header sizing
  useEffect(() => {
    const handleScrollDirection = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 10) {
        // Scrolling down
        setScrollDirection('down');
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setScrollDirection('up');
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScrollDirection, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScrollDirection);
    };
  }, [lastScrollY]);


  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitStatus !== 'idle') return;
    
    // Set submitting state
    setSubmitStatus('submitting');
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Handle form submission here
    console.log('Header waitlist submission:', { email });
    
    // Save email and submission status to sessionStorage on successful submission
    if (email.trim()) {
      sessionStorage.setItem('userEmail', email);
      sessionStorage.setItem('waitlistSubmitted', 'true');
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('waitlistUpdated'));
    }
    
    // Set submitted state and keep it permanently
    setSubmitStatus('submitted');
    
    // Trigger survey popup after 1 second if onSurveyTrigger is provided
    if (onSurveyTrigger) {
      setTimeout(() => {
        onSurveyTrigger();
      }, 1000);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`header ${scrollDirection !== 'none' ? `scroll-${scrollDirection}` : ''}`}>
      <div className="header-container">
        <div className="logo" onClick={scrollToTop}>
          <img src="/Logo Pack-28 copy.png" alt="Archaas" className="logo-image" />
        </div>
        
        {/* Mobile menu button */}
        {!isMobileMenuOpen && (
          <button 
            className="mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        )}
        
        {/* Waitlist form that appears when scrolling */}
        <div className={`waitlist-container ${showWaitlist ? 'visible' : 'hidden'}`}>
          <form className="header-waitlist-form" onSubmit={handleSubmit}>
            <div className="header-input-container">
              <input
                type="email"
                placeholder="Your Email"
                value={email}
                onChange={handleEmailChange}
                className="header-email-input"
                required
                disabled={submitStatus !== 'idle'}
              />
              <button 
                type="submit" 
                className={`header-waitlist-button ${submitStatus !== 'idle' ? 'header-waitlist-button--loading' : ''}`}
                disabled={submitStatus !== 'idle'}
              >
                {submitStatus === 'idle' && 'Join Waitlist'}
                {submitStatus === 'submitting' && (
                  <>
                    <span className="loading-spinner"></span>
                    Joining...
                  </>
                )}
                {submitStatus === 'submitted' && (
                  <>
                    <span className="success-checkmark">✓</span>
                    Thank you!
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Desktop Navigation */}
        <nav className="nav desktop-nav">
          <ul className="nav-list">
            <li><a href="#knowledge">Knowledge</a></li>
            <li><a href="#integration">Integration</a></li>
            <li><a href="#demo">Features</a></li>
            <li><a href="#about" className="nav-link-about">About</a></li>
          </ul>
        </nav>

        {/* Mobile Navigation Overlay */}
        <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}>
          <nav className={`mobile-nav ${isMobileMenuOpen ? 'active' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-nav-header">
              <div className="logo mobile-logo" onClick={scrollToTop}>
                <img src="/Logo Pack-28 copy.png" alt="Archaas" className="logo-image" />
              </div>
              <button 
                className="mobile-close-button"
                onClick={closeMobileMenu}
                aria-label="Close mobile menu"
              >
                ✕
              </button>
            </div>
            <ul className="mobile-nav-list">
              <li><a href="#knowledge" onClick={closeMobileMenu}>Knowledge</a></li>
              <li><a href="#integration" onClick={closeMobileMenu}>Integration</a></li>
              <li><a href="#demo" onClick={closeMobileMenu}>Features</a></li>
              <li><a href="#about" onClick={closeMobileMenu} className="nav-link-about">About</a></li>
            </ul>
            <div className="mobile-nav-footer">
              <form className="mobile-waitlist-form" onSubmit={handleSubmit}>
                <div className="mobile-input-container">
                  <input
                    type="email"
                    placeholder="Your Email"
                    value={email}
                    onChange={handleEmailChange}
                    className="mobile-email-input"
                    required
                    disabled={submitStatus !== 'idle'}
                  />
                  <button 
                    type="submit" 
                    className={`mobile-waitlist-button ${submitStatus !== 'idle' ? 'mobile-waitlist-button--loading' : ''}`}
                    disabled={submitStatus !== 'idle'}
                  >
                    {submitStatus === 'idle' && 'Join Waitlist'}
                    {submitStatus === 'submitting' && (
                      <>
                        <span className="loading-spinner"></span>
                        Joining...
                      </>
                    )}
                    {submitStatus === 'submitted' && (
                      <>
                        <span className="success-checkmark">✓</span>
                        Thank you!
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header; 